/**
 * Modal «Novo restaurante» do console da plataforma (#418).
 *
 * Extraído do Shell (sem mudança de layout): o Shell só monta o modal e
 * recarrega a lista em onCreated. Validação com as MESMAS regras do
 * self-serve (@self-restaurant/shared) — slug no blur e no submit; nome e
 * e-mail do dono no submit — com erro ao lado de cada campo, sugestão de
 * 1 clique (do servidor 422/409 ou, sem ela, calculada localmente) e preview
 * pratzu.com/r/<slug>. Lógica pura em create-tenant-form.ts.
 */
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { i18n, platform } from '../../lib/api.js'
import { nativeLocaleName } from '../../lib/import-locale.js'
import {
  slugFieldErrorFromApi,
  sanitizeSlugInput,
  validateSlugField,
  slugMessage,
  suggestSlugLocal,
} from '../../lib/tenant-slug.js'
import {
  slugFromName,
  selectedLocale,
  splitPlaceholder,
  validateCreateTenantForm,
} from './create-tenant-form.js'
import { Button, Input, Modal, Select, toast } from '../ui/index.js'
import { useI18n } from '../../lib/i18n.js'

const COUNTRIES = [
  { value: 'PT', labelKey: 'admin.country.pt' },
  { value: 'BR', labelKey: 'admin.country.br' },
  { value: 'US', labelKey: 'admin.country.us' },
  { value: 'ES', labelKey: 'admin.country.es' },
]

export function CreateTenantModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useI18n()
  const { data: catalogLocales } = useQuery({
    queryKey: ['catalog-locales'],
    queryFn: i18n.locales,
  })
  const localeOptions = (catalogLocales ?? []).map((l) => ({ value: l.code, label: l.native }))
  // #419: sem código de região fixo no componente — default derivado do
  // catálogo (1º idioma de base pt); sem catálogo, código nu ('pt')
  const defaultLocale =
    localeOptions.find((o) => o.value.toLowerCase().startsWith('pt'))?.value ??
    localeOptions[0]?.value ??
    'pt'

  const [saving, setSaving] = useState(false)
  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [newCountry, setNewCountry] = useState('PT')
  // '' = usuário ainda não escolheu (o seletor cai no default do catálogo);
  // a escolha explícita vence o default chegando async (#418 QA)
  const [chosenLocale, setChosenLocale] = useState('')
  const newLocale = selectedLocale(chosenLocale, defaultLocale)
  const [newOwnerEmail, setNewOwnerEmail] = useState('')
  // o slug para de seguir o nome depois da 1ª edição manual / sugestão aplicada
  const [slugTouched, setSlugTouched] = useState(false)
  const [slugError, setSlugError] = useState<{
    message: string
    suggestion: string | null
  } | null>(null)
  // região viva SÓ quando o erro nasce no blur (o foco não se move); no
  // submit e no 422/409 o foco vai ao campo e o erro é lido via
  // aria-describedby — anunciar nos dois caminhos duplicava a fala
  const [announceSlugError, setAnnounceSlugError] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  // #418 (UX): o foco do slug pós-422/409 acontece SÓ depois do re-render com
  // saving=false — focar dentro do catch atingia um input desabilitado
  const [pendingSlugFocus, setPendingSlugFocus] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  const slugRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)

  // abrir limpo a cada abertura — SÓ na abertura: o catálogo de locales chega
  // async e não pode apagar o que o usuário digitou/escolheu (#418 QA)
  useEffect(() => {
    if (!open) return
    setNewName('')
    setNewSlug('')
    setNewCountry('PT')
    setChosenLocale('')
    setNewOwnerEmail('')
    setSlugTouched(false)
    setSlugError(null)
    setAnnounceSlugError(false)
    setNameError(null)
    setEmailError(null)
    setSaving(false)
  }, [open])

  // re-foco do slug após o render em que o campo voltou a habilitado
  useEffect(() => {
    if (!pendingSlugFocus) return
    setPendingSlugFocus(false)
    slugRef.current?.focus()
  }, [pendingSlugFocus])

  // Esc/overlay não fecham o modal enquanto salva
  function requestClose() {
    if (!saving) onClose()
  }

  function changeName(name: string) {
    setNewName(name)
    setNameError(null)
    setSlugError(null)
    // nome vazio → slug vazio (nunca «restaurante»); para de sincronizar
    // depois de o usuário editar o slug ou aplicar uma sugestão
    if (!slugTouched) setNewSlug(slugFromName(name))
  }

  function changeSlug(raw: string) {
    setNewSlug(sanitizeSlugInput(raw))
    setSlugTouched(true)
    // revalida só no blur/submit; editar limpa o erro (e a sugestão velha)
    setSlugError(null)
    setAnnounceSlugError(false)
  }

  /** Valida o slug no blur — nunca a cada tecla. */
  function validateSlugFieldNow(): boolean {
    const r = validateSlugField(newSlug)
    if (r.ok) {
      setSlugError(null)
      setAnnounceSlugError(false)
      return true
    }
    setSlugError({
      message: slugMessage(r.kind, t),
      suggestion: suggestSlugLocal(newSlug),
    })
    // blur: o foco sai do campo — sem mudança de foco, a região viva anuncia
    setAnnounceSlugError(true)
    return false
  }

  function applySuggestion() {
    if (!slugError?.suggestion) return
    setNewSlug(slugError.suggestion)
    setSlugTouched(true)
    setSlugError(null)
    setAnnounceSlugError(false)
    slugRef.current?.focus()
  }

  async function send() {
    setSaving(true)
    try {
      const payload: Parameters<typeof platform.createTenant>[0] = {
        slug: newSlug,
        name: newName,
        country_code: newCountry,
        locale: newLocale,
      }
      if (newOwnerEmail) payload.owner_email = newOwnerEmail
      await platform.createTenant(payload)
      toast.success(t('admin.tenant.created_ok'))
      onCreated()
      onClose()
    } catch (err) {
      // 422/409 de slug: erro e sugestão vão para o CAMPO (não toast);
      // o comprimento enviado distingue too_long do format genérico
      const fieldError = slugFieldErrorFromApi(err, t, newSlug)
      if (fieldError) {
        setSlugError(fieldError)
        setAnnounceSlugError(false)
        setPendingSlugFocus(true)
      } else {
        // erro geral: modal mantém os dados digitados
        toast.error(t('admin.tenant.created_err'))
      }
    } finally {
      setSaving(false)
    }
  }

  function submit() {
    if (saving) return
    // validação client-side na ordem do formulário — nada é enviado com erro
    const errs = validateCreateTenantForm(
      { name: newName, slug: newSlug, ownerEmail: newOwnerEmail },
      t,
    )
    setNameError(errs.name ?? null)
    setSlugError(errs.slug ?? null)
    setAnnounceSlugError(false)
    setEmailError(errs.ownerEmail ?? null)
    if (errs.name) {
      nameRef.current?.focus()
      return
    }
    if (errs.slug) {
      slugRef.current?.focus()
      return
    }
    if (errs.ownerEmail) {
      emailRef.current?.focus()
      return
    }
    void send()
  }

  return (
    <Modal
      open={open}
      onClose={requestClose}
      title={t('admin.tenant.new')}
      footer={
        // mobile 375px: empilha com «Criar restaurante» em cima; lado a lado no resto
        <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={requestClose} disabled={saving}>
            {t('admin.common.cancel')}
          </Button>
          {/* fora do <form>: ligado por form= para passar pela validação do submit */}
          <Button form="create-tenant-form" type="submit" loading={saving}>
            {saving ? t('admin.tenant.creating') : t('admin.tenant.create')}
          </Button>
        </div>
      }
    >
      <form
        id="create-tenant-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        className="space-y-4"
      >
        <Input
          ref={nameRef}
          label={t('admin.tenant.name')}
          value={newName}
          onChange={(e) => changeName(e.target.value)}
          placeholder={t('admin.tenant.name_ph')}
          disabled={saving}
          required
          error={nameError ?? ''}
        />
        <div>
          {/* aria-invalid + aria-describedby (erro/hint) são cableados pelo Input */}
          <Input
            ref={slugRef}
            id="tenant-slug"
            label={t('admin.tenant.slug')}
            value={newSlug}
            onChange={(e) => changeSlug(e.target.value)}
            onBlur={validateSlugFieldNow}
            placeholder={t('admin.tenant.slug_ph')}
            disabled={saving}
            required
            // slug é sempre LTR, mesmo em idioma RTL do painel
            dir="ltr"
            error={slugError?.message ?? ''}
            hint={t('admin.tenant.slug_hint')}
          />
          {/* fora da região viva: sugestão e preview não se repetem a cada tecla */}
          {slugError?.suggestion && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="min-h-11"
              onClick={applySuggestion}
              disabled={saving}
            >
              {/* só o slug vai isolado em bdi LTR; o rótulo segue a direção da página */}
              {(() => {
                const [before, after] = splitPlaceholder(
                  t('admin.tenant.slug_use_suggestion'),
                  'suggestion',
                )
                return (
                  <>
                    {before}
                    <bdi dir="ltr">{slugError.suggestion}</bdi>
                    {after}
                  </>
                )
              })()}
            </Button>
          )}
          {!slugError && newSlug && (
            <p className="text-xs text-gray-500">
              <bdi dir="ltr">{t('admin.tenant.slug_preview', { slug: newSlug })}</bdi>
            </p>
          )}
          {/* região viva SÓ para erro de blur: no submit e no 422/409 o foco é
              movido ao campo e o erro é lido via aria-describedby — anunciar
              pelos dois caminhos duplicava a fala (leitor de tela) */}
          <div id="tenant-slug-live" role="status" aria-live="polite" className="sr-only">
            {announceSlugError && slugError?.message}
          </div>
        </div>
        {/* mobile 375px: País e Idioma em uma coluna abaixo de sm */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label={t('admin.tenant.country')}
            value={newCountry}
            onChange={(e) => setNewCountry(e.target.value)}
            options={COUNTRIES.map((c) => ({ value: c.value, label: t(c.labelKey) }))}
            disabled={saving}
          />
          <Select
            label={t('admin.tenant.locale')}
            value={newLocale}
            onChange={(e) => setChosenLocale(e.target.value)}
            options={
              localeOptions.length
                ? localeOptions
                : // sem catálogo: código nu 'pt' com nome do Intl (nunca rótulo cravado)
                  [{ value: 'pt', label: nativeLocaleName('pt', catalogLocales ?? []) }]
            }
            optionLang
            disabled={saving}
          />
        </div>
        <Input
          ref={emailRef}
          label={t('admin.tenant.owner_email')}
          type="email"
          value={newOwnerEmail}
          onChange={(e) => {
            setNewOwnerEmail(e.target.value)
            setEmailError(null)
          }}
          placeholder={t('admin.tenant.owner_email_ph')}
          disabled={saving}
          hint={t('admin.tenant.owner_email_hint')}
          error={emailError ?? ''}
        />
      </form>
    </Modal>
  )
}
